import { css } from '@emotion/react';
import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';

import BackgroundGlow from '~/common/components/BackgroundGlow';
import Container from '~/common/layouts/Container';
import Flex from '~/common/layouts/Flex';
import { APP_MODE, ViteAppMode } from '~/common/utils/env.util.ts';

interface ErrorProps {
  title?: string;
  message?: string;
  status?: number;
}

const isProduction = APP_MODE === ViteAppMode.PRODUCTION;

const DEFAULT_TITLE = 'Ooops!';
const DEFAULT_MESSAGE = 'Something went wrong!';

function ErrorPage({ title, message, status }: ErrorProps) {
  const navigate = useNavigate();

  const errorTitle = isProduction ? DEFAULT_TITLE : title || DEFAULT_TITLE;
  const errorMessage = isProduction ? DEFAULT_MESSAGE : message || DEFAULT_MESSAGE;
  const displayStatus = isProduction ? undefined : status;

  return (
    <div css={styles.page}>
      <BackgroundGlow />
      <Container>
        <Flex css={styles.wrapper} direction="column" items="center" justify="center">
          {displayStatus && <h1 css={styles.status}>{displayStatus}</h1>}
          <h2 css={styles.title}>{errorTitle}</h2>
          <p css={styles.message}>{errorMessage}</p>

          <Button css={styles.button} size="large" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </Flex>
      </Container>
    </div>
  );
}

const styles = {
  page: css`
    position: relative;
    min-height: 100vh;
    background: #0b0714;
    overflow: hidden;
  `,

  wrapper: css`
    position: relative;
    z-index: 1;
    width: 100%;
    min-height: 100vh;
    text-align: center;
    color: #fff;
  `,

  status: css`
    font-size: 140px;
    font-weight: 800;
    line-height: 1;
    margin-bottom: 8px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(200, 180, 255, 0.8) 100%);
    -webkit-background-clip: text;
    color: transparent;

    @media (max-width: 640px) {
      font-size: 100px;
    }
  `,

  title: css`
    font-size: 36px;
    font-weight: 700;
    margin-bottom: 12px;
    color: rgba(255, 255, 255, 0.95);

    @media (max-width: 640px) {
      font-size: 28px;
    }
  `,

  message: css`
    font-size: 18px;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 32px;

    @media (max-width: 640px) {
      font-size: 16px;
    }
  `,

  button: css`
    height: 48px;
    padding: 0 32px;
    border-radius: 12px;
    font-weight: 600;
    color: white;
    background: linear-gradient(135deg, #00b4f0 0%, #1466e8 100%);
    border: none;
    transition: 0.2s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0, 140, 230, 0.4);
    }
  `,
};

export default ErrorPage;
